import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Upload, User, ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { getCoverUrl } from '../utils/coverUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const EditAuthorProfileDialog = ({ open, onOpenChange, onSaved, targetUserId = null }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [form, setForm] = useState({ bio: '', website: '', instagram: '', twitter: '', facebook: '' });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const qs = targetUserId ? `?user_id=${encodeURIComponent(targetUserId)}` : '';

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/authors/me/profile${qs}`, auth);
        const p = res.data || {};
        setForm({
          bio: p.bio || '',
          website: p.website || '',
          instagram: p.instagram || '',
          twitter: p.twitter || '',
          facebook: p.facebook || '',
        });
        setPhotoUrl(p.photo_url || null);
        setBackgroundUrl(p.background_url || null);
      } catch (e) {
        toast({
          title: 'Could not load your profile',
          description: e.response?.data?.detail || e.message,
          variant: 'destructive',
        });
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadImage = async (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'File must be an image', variant: 'destructive' });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast({ title: 'Image must be under 8MB', variant: 'destructive' });
      return;
    }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/authors/me/${kind}${qs}`, fd, auth);
      if (kind === 'photo') setPhotoUrl(res.data.photo_url);
      else setBackgroundUrl(res.data.background_url);
      toast({ title: kind === 'photo' ? 'Profile photo updated' : 'Background photo updated' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/authors/me/profile${qs}`, form, auth);
      const synced = res.data?.synced_books || 0;
      toast({
        title: 'Profile updated',
        description: synced > 0
          ? `Your author page is live — details also synced to ${synced} book page${synced > 1 ? 's' : ''}.`
          : 'Your public author page now shows the new details.',
      });
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-author-profile-dialog">
        <DialogHeader>
          <DialogTitle>Edit Author Profile</DialogTitle>
          <DialogDescription className="text-gray-400">
            Photo uploads save instantly; other fields save when you press Save Changes. Changes go live on your public author page immediately.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-red-500" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Background banner */}
            <div>
              <Label className="text-gray-300">Background photo</Label>
              <div className="mt-2 relative aspect-[3/1] rounded-lg overflow-hidden bg-white/5 border border-white/10">
                {backgroundUrl ? (
                  <img src={getCoverUrl(backgroundUrl)} alt="Background" className="w-full h-full object-cover" data-testid="edit-bg-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600">
                    <ImageIcon className="h-8 w-8" />
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                id="author-bg-input"
                className="hidden"
                data-testid="author-bg-file-input"
                onChange={(e) => { uploadImage('background', e.target.files?.[0]); e.target.value = ''; }}
              />
              <label
                htmlFor="author-bg-input"
                className="inline-flex items-center gap-1.5 mt-2 text-sm bg-white/10 hover:bg-white/15 text-gray-200 rounded-full px-4 py-2 cursor-pointer transition-colors"
                data-testid="upload-background-btn"
              >
                {uploading === 'background' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {backgroundUrl ? 'Change background' : 'Upload background'}
              </label>
            </div>

            {/* Profile photo */}
            <div>
              <Label className="text-gray-300">Profile photo</Label>
              <div className="mt-2 flex items-center gap-4">
                {photoUrl ? (
                  <img src={getCoverUrl(photoUrl)} alt="Profile" className="w-20 h-20 rounded-xl object-cover border border-white/15" data-testid="edit-photo-preview" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <User className="h-8 w-8 text-gray-600" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  id="author-photo-input"
                  className="hidden"
                  data-testid="author-photo-file-input"
                  onChange={(e) => { uploadImage('photo', e.target.files?.[0]); e.target.value = ''; }}
                />
                <label
                  htmlFor="author-photo-input"
                  className="inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/15 text-gray-200 rounded-full px-4 py-2 cursor-pointer transition-colors"
                  data-testid="upload-photo-btn"
                >
                  {uploading === 'photo' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {photoUrl ? 'Change photo' : 'Upload photo'}
                </label>
              </div>
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="author-bio-input" className="text-gray-300">Bio</Label>
              <Textarea
                id="author-bio-input"
                value={form.bio}
                onChange={set('bio')}
                rows={5}
                maxLength={3000}
                placeholder="Tell readers about yourself..."
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                data-testid="edit-bio-textarea"
              />
            </div>

            {/* Website */}
            <div>
              <Label htmlFor="author-website-input" className="text-gray-300">Website</Label>
              <Input
                id="author-website-input"
                value={form.website}
                onChange={set('website')}
                placeholder="https://yourwebsite.com"
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                data-testid="edit-website-input"
              />
            </div>

            {/* Socials */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label htmlFor="author-instagram-input" className="text-gray-300">Instagram</Label>
                <Input
                  id="author-instagram-input"
                  value={form.instagram}
                  onChange={set('instagram')}
                  placeholder="@yourhandle or full link"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                  data-testid="edit-instagram-input"
                />
              </div>
              <div>
                <Label htmlFor="author-twitter-input" className="text-gray-300">X (Twitter)</Label>
                <Input
                  id="author-twitter-input"
                  value={form.twitter}
                  onChange={set('twitter')}
                  placeholder="@yourhandle or full link"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                  data-testid="edit-twitter-input"
                />
              </div>
              <div>
                <Label htmlFor="author-facebook-input" className="text-gray-300">Facebook</Label>
                <Input
                  id="author-facebook-input"
                  value={form.facebook}
                  onChange={set('facebook')}
                  placeholder="Page name or full link"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                  data-testid="edit-facebook-input"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-300" data-testid="edit-profile-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading} className="bg-red-600 hover:bg-red-500" data-testid="edit-profile-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAuthorProfileDialog;
